import { useMemo, useState } from 'react';
import { Bot } from 'lucide-react';
import { Container, PageHeader, Section } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Badge, Button, Skeleton } from '@vnetwork/ui/primitives';
import { ConfirmDialog, useToast } from '@vnetwork/ui/feedback';
import {
  useBotOpenApiRules,
  useDeleteBotOpenApiRule,
  type BotRule,
} from '@/api/waap';
import { WaapConsoleBreadcrumbs } from '@/pages/services/waap/WaapConsoleBreadcrumbs';
import { useWaapWebsite } from '@/pages/services/waap/waap-context';
import { errMessage } from '@/pages/services/waap/waap-util';
import styles from './BotOpenApiTab.module.css';

/**
 * Bot Management served via the PUBLIC Open-API (reference slice). From the FE's
 * perspective this is an ordinary WAAP feature — it calls the same
 * `/waf/eagle/...` endpoints; the servicemgmt BE is the one that resolves it
 * through GetPublicWAAP (root conversant SDK over HMAC) instead of the private
 * portal. The FE is intentionally surface-agnostic.
 */
export function BotOpenApiTab() {
  const { selected, isLoading: loadingSites } = useWaapWebsite();
  const domainId = selected?.domainId;
  const { show } = useToast();
  const [pendingDelete, setPendingDelete] = useState<BotRule | null>(null);

  const { data: rules = [], isLoading } = useBotOpenApiRules(domainId);

  const del = useDeleteBotOpenApiRule(domainId ?? '', {
    onSuccess: () => {
      show({ tone: 'success', title: 'Bot rule deleted' });
      setPendingDelete(null);
    },
    onError: (e) => show({ tone: 'danger', title: 'Delete failed', description: errMessage(e) }),
  });

  const columns = useMemo<ColumnDef<BotRule>[]>(
    () => [
      { accessorKey: 'id', header: 'ID', cell: ({ row }) => <span className={styles.muted}>{row.original.id}</span> },
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => <span className={styles.name}>{row.original.name}</span>,
      },
      {
        id: 'silence',
        header: 'Silent Challenge',
        cell: ({ row }) =>
          row.original.silence_challenge_enabled ? (
            <Badge tone="success" appearance="subtle">
              {row.original.silence_challenge_action || 'on'}
            </Badge>
          ) : (
            <span className={styles.muted}>Off</span>
          ),
      },
      {
        id: 'websites',
        header: 'Websites',
        cell: ({ row }) => row.original.websites?.length || <span className={styles.muted}>—</span>,
      },
      {
        id: 'actions',
        header: 'Operation',
        cell: ({ row }) => (
          <div className={styles.actions}>
            <Button variant="ghost" size="sm" onClick={() => setPendingDelete(row.original)}>
              Delete
            </Button>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <Container size="xl">
      <PageHeader
        breadcrumbs={<WaapConsoleBreadcrumbs trail={[{ label: 'Bot Management (Open-API)' }]} />}
        title="Bot Management"
        description="Bot rules challenge or block automated traffic."
      />

      <div className={styles.banner}>
        Served via the public Open-API surface — the FE contract is identical to the private features.
      </div>

      <Section title="Bot Rules">
        {!domainId ? (
          <EmptyState
            icon={<Bot size={20} />}
            title={loadingSites ? 'Loading…' : 'No protected websites'}
            description={
              loadingSites ? 'Resolving your WAAP websites.' : 'Add a WAAP website first.'
            }
          />
        ) : isLoading ? (
          <div className={styles.skeletonList}>
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} style={{ height: 48 }} />
            ))}
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={rules}
            emptyState={
              <EmptyState size="sm" icon={<Bot size={20} />} title="No bot rules" description="No bot rules on this account yet." />
            }
          />
        )}
      </Section>

      <ConfirmDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete bot rule?"
        description={`Delete "${pendingDelete?.name}"? This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        loading={del.isPending}
        onConfirm={() => pendingDelete && del.mutate(pendingDelete.id)}
      />
    </Container>
  );
}
